import { useEffect, useState } from "react";
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Check,
  CirclePlay,
  CreditCard,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  TabletSmartphone,
} from "lucide-react";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";

import {
  accessCards,
  bonuses,
  checkoutUrl,
  courseName,
  courseBullets,
  heroVideoUrl,
  ipadBenefits,
  logicImages,
  metrics,
  modules,
  objections,
  producer,
  promisePillars,
  showcaseCards,
  testimonials,
  trustPills,
} from "./content";
import { ModuleCard } from "./ModuleCard";

const CheckoutButton = ({ children, testId, className = "" }) => (
  <Button
    asChild
    className={`logic-cta h-12 rounded-full px-6 text-sm font-semibold transition-transform duration-300 ${className}`}
    data-testid={testId}
  >
    <a href={checkoutUrl} target="_blank" rel="noreferrer" data-testid={`${testId}-link`}>
      {children}
    </a>
  </Button>
);

const SecondaryLink = ({ href, testId, children }) => {
  const isExternal = href.startsWith("http");

  return (
    <a
      href={href}
      className="logic-cta-secondary inline-flex h-12 items-center justify-center rounded-full px-6 text-sm font-medium text-white/88 transition duration-300 hover:border-white/25 hover:bg-white/8"
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noreferrer" : undefined}
      data-testid={testId}
    >
      {children}
    </a>
  );
};

export const LandingPage = () => {
  const [testimonialPage, setTestimonialPage] = useState(0);
  const testimonialPageCount = Math.max(1, Math.ceil(testimonials.length / 3));
  const visibleTestimonials = Array.from({ length: Math.min(3, testimonials.length) }, (_, offset) => {
    return testimonials[(testimonialPage * 3 + offset) % testimonials.length];
  });
  const whatsappUrl =
    "https://wa.me/5561985533770?text=Ol%C3%A1.%20Me%20interessei%20pelo%20curso%20de%20Logic%20Pro%20e%20quero%20saber%20mais%20detalhes.";

  useEffect(() => {
    document.documentElement.lang = "pt-BR";
    document.title = `${courseName} | Método completo para dominar o Logic Pro`;
    const canonicalUrl = window.location.href.split("#")[0];
    const hideEmergentBadge = () => {
      const badge = document.getElementById("emergent-badge");
      if (badge) {
        badge.style.setProperty("display", "none", "important");
        badge.setAttribute("aria-hidden", "true");
      }
    };

    hideEmergentBadge();

    const badgeObserver = new MutationObserver(() => hideEmergentBadge());
    badgeObserver.observe(document.body, { childList: true, subtree: true });

    const upsertMeta = (attribute, key, content) => {
      const selector = `meta[${attribute}="${key}"]`;
      const meta = document.querySelector(selector) || document.createElement("meta");
      meta.setAttribute(attribute, key);
      meta.setAttribute("content", content);
      document.head.appendChild(meta);
    };

    const upsertLink = (rel, href) => {
      const link = document.querySelector(`link[rel="${rel}"]`) || document.createElement("link");
      link.setAttribute("rel", rel);
      link.setAttribute("href", href);
      document.head.appendChild(link);
    };

    upsertMeta("name", "description", "Curso Completo de Logic Pro com método prático, aula grátis de Flex Pitch, gravação, edição, mixagem, masterização e IA.");
    upsertMeta("name", "keywords", "curso completo de Logic Pro, curso Logic Pro online, aprender Logic Pro, Flex Pitch, produção musical Logic Pro");
    upsertMeta("name", "robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    upsertMeta("property", "og:title", `${courseName} | Método completo para dominar o Logic Pro`);
    upsertMeta("property", "og:description", "Conheça o Curso Completo de Logic Pro, veja a aula grátis de Flex Pitch e aprenda produção musical com método prático.");
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", logicImages.hero);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", courseName);
    upsertMeta("name", "twitter:description", "Curso completo com videoaula grátis, gravação, edição, mixagem, masterização e recursos atuais do Logic Pro.");
    upsertMeta("name", "twitter:image", logicImages.hero);
    upsertLink("canonical", canonicalUrl);

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Course",
      name: courseName,
      description: "Curso online completo de Logic Pro com aula demonstrativa de Flex Pitch, foco em produção musical, gravação, edição, mixagem, masterização e IA.",
      provider: {
        "@type": "Person",
        name: producer.name,
      },
      creator: {
        "@type": "Person",
        name: producer.name,
      },
      inLanguage: "pt-BR",
      keywords: ["curso Logic Pro", "aprender Logic Pro", "produção musical Logic", "mixagem", "masterização"],
      offers: {
        "@type": "Offer",
        price: "139.90",
        priceCurrency: "BRL",
        url: checkoutUrl,
        availability: "https://schema.org/InStock",
      },
    };

    const structuredDataScript = document.getElementById("logic-course-jsonld") || document.createElement("script");
    structuredDataScript.id = "logic-course-jsonld";
    structuredDataScript.type = "application/ld+json";
    structuredDataScript.textContent = JSON.stringify(structuredData);
    document.head.appendChild(structuredDataScript);

    const elements = document.querySelectorAll('[data-reveal="true"]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }
        });
      },
      { threshold: 0.14 },
    );

    elements.forEach((element) => observer.observe(element));

    return () => {
      observer.disconnect();
      badgeObserver.disconnect();
    };
  }, []);

  return (
    <div className="logic-page text-white">
      <div className="floating-orb left-[-120px] top-[10rem] h-[18rem] w-[18rem] bg-cyan-400/35" />
      <div className="floating-orb right-[-80px] top-[26rem] h-[20rem] w-[20rem] bg-fuchsia-400/25" />

      <header className="logic-nav">
        <div className="logic-shell flex items-center justify-between gap-4 py-4">
          <div className="space-y-1" data-testid="nav-brand-block">
            <p className="text-[0.65rem] uppercase tracking-[0.34em] text-white/45" data-testid="nav-brand-label">
              Formação prática em produção musical
            </p>
            <p className="text-sm font-medium text-white/88" data-testid="nav-brand-title">
              {courseName}
            </p>
          </div>

          <nav className="hidden items-center gap-6 text-sm text-white/62 md:flex" data-testid="nav-links">
            <a href="#curso" className="transition hover:text-white" data-testid="nav-link-curso">Curso</a>
            <a href="#modulos" className="transition hover:text-white" data-testid="nav-link-modulos">Módulos</a>
            <a href="#produtor" className="transition hover:text-white" data-testid="nav-link-produtor">Produtor</a>
            <a href="#oferta" className="transition hover:text-white" data-testid="nav-link-oferta">Oferta</a>
          </nav>

          <CheckoutButton testId="nav-cta-button" className="hidden md:inline-flex">
            Quero entrar no curso completo
          </CheckoutButton>
        </div>
      </header>

      <main>
        <section className="logic-section logic-section--hero pt-10 md:pt-16" id="hero">
          <div className="logic-shell">
            <div className="reveal-block is-visible mx-auto max-w-4xl text-center">
              <Badge className="mb-6 max-w-2xl rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[0.62rem] uppercase tracking-[0.28em] text-cyan-100 whitespace-normal leading-relaxed" data-testid="hero-badge">
                Aulas atualizadas mostrando todos os novos recursos do Logic Pro
              </Badge>

              <h1 className="text-4xl font-semibold leading-[0.95] text-white sm:text-5xl lg:text-7xl" data-testid="hero-title">
                Curso Completo de <span className="logic-gradient-text whitespace-nowrap">Logic Pro</span>
              </h1>

              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <CheckoutButton testId="hero-primary-cta-button">👉 Quero entrar no curso completo</CheckoutButton>
                <SecondaryLink href="#oferta" testId="hero-secondary-cta-link">
                  Ver conteúdo e oferta
                </SecondaryLink>
              </div>
            </div>

            <div className="reveal-block mx-auto mt-8 max-w-7xl" data-reveal="true">
              <div className="mb-5 text-center" data-testid="hero-video-caption-panel">
                <p className="text-xs uppercase tracking-[0.28em] text-cyan-200" data-testid="hero-video-caption-label">
                  Aula grátis
                </p>
                <p className="mt-2 text-2xl font-semibold text-white md:text-3xl" data-testid="hero-video-caption-title">
                  Como Afinar Voz Usando o Flex Pitch
                </p>
              </div>

              <div className="logic-hero-screen aspect-video bg-black" data-testid="hero-visual-wrapper">
                <iframe
                  src={heroVideoUrl}
                  title="Aula grátis - Como Afinar Voz Usando o Flex Pitch"
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  data-testid="hero-video-iframe"
                />
              </div>

              <div className="mt-4 flex justify-center">
                <SecondaryLink href="https://youtu.be/33Nclc1Ae9c" testId="hero-video-youtube-link">
                  Assistir no YouTube
                </SecondaryLink>
              </div>
            </div>
          </div>
        </section>

        <section className="logic-section logic-section--compact-top" id="promessa">
          <div className="logic-shell">
            <div className="reveal-block mx-auto max-w-4xl text-center" data-reveal="true">
              <h2 className="mt-5 text-4xl font-semibold leading-tight text-white sm:text-5xl" data-testid="promise-title">
                Você aprende Logic Pro com clareza, sequência e aplicação prática.
              </h2>
              <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white md:text-lg" data-testid="promise-description">
                Em vez de tentar descobrir tudo sozinho, você segue um curso completo que mostra o que usar, quando usar e como construir um fluxo seguro dentro do Logic Pro.
              </p>
              <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="metrics-grid">
                {metrics.map((metric, index) => (
                  <div key={metric.label} className="logic-panel rounded-[1.5rem] p-5 text-center" data-testid={`metric-card-${index + 1}`}>
                    <p className="text-2xl font-semibold text-white" data-testid={`metric-value-${index + 1}`}>{metric.value}</p>
                    <p className="mt-1 text-sm text-white/60" data-testid={`metric-label-${index + 1}`}>{metric.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 grid gap-4 reveal-block md:grid-cols-3" data-reveal="true">
              {promisePillars.map((pillar, index) => (
                <Card key={pillar.title} className="logic-panel rounded-[1.75rem] border-0 text-center" data-testid={`promise-card-${index + 1}`}>
                  <CardContent className="p-6 md:p-7">
                    <p className="text-lg font-medium text-white" data-testid={`promise-card-title-${index + 1}`}>{pillar.title}</p>
                    <p className="mt-3 text-base leading-relaxed text-white" data-testid={`promise-card-text-${index + 1}`}>{pillar.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="logic-section" id="curso">
          <div className="logic-shell">
            <div className="reveal-block mx-auto max-w-4xl text-center" data-reveal="true">
              <p className="text-sm uppercase tracking-[0.34em] text-white/42" data-testid="course-label">
                Sobre o curso
              </p>
              <h2 className="mt-5 text-4xl font-semibold leading-tight text-white sm:text-5xl" data-testid="course-title">
                Um curso completo para sair do zero e dominar o fluxo real do Logic Pro.
              </h2>
              <p className="mx-auto mt-6 max-w-3xl text-base leading-relaxed text-white/76 md:text-lg" data-testid="course-description">
                Você aprende a configurar corretamente, navegar com clareza, gravar, editar, organizar sessões, usar recursos inteligentes, mixar e finalizar músicas em um treinamento pensado para quem quer domínio real do software.
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="course-bullets-list">
                {courseBullets.map((item, index) => (
                  <div key={item} className="logic-panel rounded-[1.35rem] px-4 py-5 text-center" data-testid={`course-bullet-${index + 1}`}>
                    <span className="mx-auto inline-flex rounded-full border border-cyan-300/30 bg-cyan-300/10 p-1 text-cyan-200">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <p className="mt-3 text-sm leading-relaxed text-white/84 md:text-base">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="reveal-block mt-10 grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-reveal="true">
              {showcaseCards.map((card, index) => (
                <div key={card.title} className="logic-image-frame overflow-hidden" data-testid={`showcase-card-${index + 1}`}>
                  <div className="aspect-[5/6] overflow-hidden">
                    <img src={card.image} alt={card.title} className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-testid={`showcase-card-image-${index + 1}`} />
                  </div>
                  <div className="p-5 text-center">
                    <p className="text-lg font-medium text-white" data-testid={`showcase-card-title-${index + 1}`}>{card.title}</p>
                    <p className="mt-2 text-sm leading-relaxed text-white/82" data-testid={`showcase-card-text-${index + 1}`}>{card.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="logic-section" id="acesso-logic">
          <div className="logic-shell grid gap-8 lg:grid-cols-[1.04fr_0.96fr] lg:items-center">
            <div className="reveal-block text-center" data-reveal="true">
              <h2 className="mt-5 text-4xl font-semibold leading-tight text-white sm:text-5xl" data-testid="access-title">
                Hoje já existe uma forma acessível de começar no Logic Pro.
              </h2>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/76 md:text-lg" data-testid="access-description">
                Você pode acessar o Logic Pro pelo Apple Creator Studio e iniciar seus estudos com investimento reduzido, sem precisar montar toda a estrutura antes de entrar no curso e começar a praticar.
              </p>

              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <span className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/72" data-testid="access-pill-1">👉 Sem comprar software caro</span>
                <span className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/72" data-testid="access-pill-2">👉 Sem setup gigante para começar</span>
              </div>

              <div className="logic-image-frame mt-8 overflow-hidden" data-testid="access-image-frame">
                <div className="aspect-[16/9] bg-black">
                  <img src={logicImages.creatorStudio} alt="Apple Creator Studio com apps criativos" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-testid="access-image" />
                </div>
              </div>
            </div>

            <div className="reveal-block grid gap-4" data-reveal="true">
              {accessCards.map((card, index) => (
                <Card key={card.label} className="logic-panel rounded-[1.75rem] border-0 text-center" data-testid={`access-card-${index + 1}`}>
                  <CardContent className="p-6 md:p-7">
                    <span className="mx-auto inline-flex rounded-full border border-white/10 bg-white/5 p-3 text-cyan-200">
                      <CreditCard className="h-5 w-5" />
                    </span>
                    <p className="mt-4 text-sm uppercase tracking-[0.3em] text-white/40" data-testid={`access-card-label-${index + 1}`}>{card.label}</p>
                    <p className="mt-2 text-2xl font-semibold text-white" data-testid={`access-card-value-${index + 1}`}>{card.value}</p>
                    <p className="mt-2 text-sm leading-relaxed text-white/82" data-testid={`access-card-detail-${index + 1}`}>{card.detail}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="logic-section" id="ipad">
          <div className="logic-shell grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div className="reveal-block order-2 lg:order-1" data-reveal="true">
              <div className="logic-image-frame overflow-hidden" data-testid="ipad-image-frame">
                <div className="aspect-[16/10] bg-black">
                  <img src={logicImages.ipad} alt="Logic Pro funcionando no iPad e Mac" className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-testid="ipad-image" />
                </div>
              </div>
            </div>

            <div className="reveal-block order-1 text-center lg:order-2" data-reveal="true">
              <p className="text-sm uppercase tracking-[0.34em] text-white/42" data-testid="ipad-label">
                Logic no iPad
              </p>
              <h2 className="mt-5 text-4xl font-semibold leading-tight text-white sm:text-5xl" data-testid="ipad-title">
                O curso também prepara você para usar o Logic no iPad.
              </h2>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/76 md:text-lg" data-testid="ipad-description">
                Além do Mac, o Logic Pro também funciona no iPad com uma experiência moderna e fluida. O curso te ajuda a entender os fundamentos do software para criar com mais liberdade em diferentes contextos.
              </p>

              <div className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="ipad-benefits-grid">
                {ipadBenefits.map((benefit, index) => (
                  <Card key={benefit} className="logic-panel rounded-[1.5rem] border-0 text-center" data-testid={`ipad-benefit-card-${index + 1}`}>
                    <CardContent className="p-5">
                      <div className="mb-3 inline-flex rounded-full border border-white/10 bg-white/5 p-2 text-cyan-200">
                        <TabletSmartphone className="h-4 w-4" />
                      </div>
                      <p className="text-sm leading-relaxed text-white md:text-base" data-testid={`ipad-benefit-text-${index + 1}`}>{benefit}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="logic-section" id="modulos">
          <div className="logic-shell">
            <div className="reveal-block mx-auto max-w-3xl text-center" data-reveal="true">
              <p className="text-sm uppercase tracking-[0.34em] text-white/42" data-testid="modules-label">
                Módulos do curso
              </p>
              <h2 className="mt-5 text-4xl font-semibold leading-tight text-white sm:text-5xl" data-testid="modules-title">
                Do setup à finalização: uma trilha completa dentro do Logic Pro.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-white/66 md:text-lg" data-testid="modules-description">
                Cada etapa do curso foi desenhada para conduzir você do entendimento básico até a produção, edição, mixagem e masterização com uma lógica contínua e aplicável.
              </p>
            </div>

            <div className="mt-10 grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="modules-grid">
              {modules.map((module, index) => (
                <div key={module.title} className="reveal-block" data-reveal="true" style={{ transitionDelay: `${index * 40}ms` }}>
                  <ModuleCard index={index} module={module} />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="logic-section" id="produtor">
          <div className="logic-shell grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
            <div className="reveal-block" data-reveal="true">
              <div className="logic-image-frame overflow-hidden" data-testid="producer-image-frame">
                <div className="aspect-[4/5] bg-black sm:aspect-[16/12] lg:aspect-[4/5]">
                  <img src={producer.image} alt={producer.name} className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-testid="producer-image" />
                </div>
              </div>
            </div>

            <div className="reveal-block text-center" data-reveal="true">
              <p className="text-sm uppercase tracking-[0.34em] text-white/42" data-testid="producer-label">
                Sobre o produtor
              </p>
              <h2 className="mt-5 text-4xl font-semibold leading-tight text-white sm:text-5xl" data-testid="producer-title">
                Aprenda com Mariano Júnior, produtor que une prática de estúdio e experiência de ensino.
              </h2>
              <p className="mt-4 text-lg text-white/82" data-testid="producer-role">
                {producer.role}
              </p>
              <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/76 md:text-lg" data-testid="producer-bio">
                {producer.bio}
              </p>

              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                <Card className="logic-panel rounded-[1.5rem] border-0" data-testid="producer-stat-card-1">
                  <CardContent className="p-5">
                    <p className="text-2xl font-semibold text-white" data-testid="producer-stat-value-1">20+ anos</p>
                    <p className="mt-1 text-sm text-white/60" data-testid="producer-stat-label-1">de experiência prática</p>
                  </CardContent>
                </Card>
                <Card className="logic-panel rounded-[1.5rem] border-0" data-testid="producer-stat-card-2">
                  <CardContent className="p-5">
                    <p className="text-2xl font-semibold text-white" data-testid="producer-stat-value-2">+6.000</p>
                    <p className="mt-1 text-sm text-white/60" data-testid="producer-stat-label-2">alunos formados</p>
                  </CardContent>
                </Card>
                <Card className="logic-panel rounded-[1.5rem] border-0" data-testid="producer-stat-card-3">
                  <CardContent className="p-5">
                    <p className="text-2xl font-semibold text-white" data-testid="producer-stat-value-3">Estúdio Hertz</p>
                    <p className="mt-1 text-sm text-white/60" data-testid="producer-stat-label-3">ecossistema real de criação</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="logic-section" id="depoimentos">
          <div className="logic-shell">
            <div className="reveal-block mx-auto max-w-3xl text-center" data-reveal="true">
              <h2 className="mt-5 text-4xl font-semibold leading-tight text-white sm:text-5xl" data-testid="testimonials-title">
                Quem já estudou com Mariano percebe a diferença na prática.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-white/76 md:text-lg" data-testid="testimonials-description">
                O curso se apoia em experiência real de produção, didática aplicada e feedback de alunos que já tiveram contato com o método do Estúdio Hertz.
              </p>
            </div>

            <div className="mt-8 flex items-center justify-between gap-4" data-testid="testimonials-carousel-controls">
              <div className="flex items-center gap-2" data-testid="testimonials-carousel-dots">
                {Array.from({ length: testimonialPageCount }).map((_, index) => (
                  <button
                    key={`testimonial-dot-${index + 1}`}
                    type="button"
                    onClick={() => setTestimonialPage(index)}
                    className={`h-2.5 rounded-full transition-all ${testimonialPage === index ? "w-8 bg-white" : "w-2.5 bg-white/28"}`}
                    data-testid={`testimonial-dot-${index + 1}`}
                    aria-label={`Ir para página ${index + 1} do carrossel`}
                  />
                ))}
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setTestimonialPage((prev) => (prev - 1 + testimonialPageCount) % testimonialPageCount)}
                  className="logic-cta-secondary inline-flex h-11 w-11 items-center justify-center rounded-full text-white"
                  data-testid="testimonials-prev-button"
                  aria-label="Ver depoimentos anteriores"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setTestimonialPage((prev) => (prev + 1) % testimonialPageCount)}
                  className="logic-cta-secondary inline-flex h-11 w-11 items-center justify-center rounded-full text-white"
                  data-testid="testimonials-next-button"
                  aria-label="Ver próximos depoimentos"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mt-6 grid gap-5 md:grid-cols-3" data-testid="testimonials-grid">
              {visibleTestimonials.map((testimonial, index) => (
                <div key={testimonial.author} className="reveal-block testimonial-card logic-image-frame overflow-hidden" data-reveal="true" data-testid={`testimonial-card-${index + 1}`}>
                  <div className="aspect-[3/5] overflow-hidden bg-black">
                    <img src={testimonial.image} alt={testimonial.author} className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-testid={`testimonial-image-${index + 1}`} />
                  </div>
                  <div className="p-5 text-center">
                    <p className="text-sm uppercase tracking-[0.24em] text-white/42" data-testid={`testimonial-author-${index + 1}`}>{testimonial.author}</p>
                    <p className="mt-3 text-sm leading-relaxed text-white" data-testid={`testimonial-quote-${index + 1}`}>{testimonial.quote}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="logic-section" id="objeções">
          <div className="logic-shell">
            <div className="reveal-block mx-auto max-w-4xl text-center" data-reveal="true">
              <p className="text-sm uppercase tracking-[0.34em] text-white/42" data-testid="objections-label">
                Perguntas frequentes
              </p>
              <h2 className="mt-5 text-4xl font-semibold leading-tight text-white sm:text-5xl" data-testid="objections-title">
                Dúvidas comuns de quem quer fazer um curso completo de Logic Pro.
              </h2>
              <p className="mx-auto mt-6 max-w-3xl text-base leading-relaxed text-white/76 md:text-lg" data-testid="objections-description">
                Aqui estão respostas objetivas para as perguntas mais comuns de quem quer aprender Logic Pro com método, clareza e aplicação real.
              </p>
            </div>

            <div className="reveal-block mx-auto mt-10 max-w-4xl" data-reveal="true">
              <Accordion type="single" collapsible className="logic-panel rounded-[2rem] px-6 py-3" data-testid="objections-accordion">
                {objections.map((item, index) => (
                  <AccordionItem key={item.title} value={`item-${index + 1}`} className="border-white/8" data-testid={`objection-item-${index + 1}`}>
                    <AccordionTrigger className="text-left text-lg text-white hover:no-underline" data-testid={`objection-trigger-${index + 1}`}>
                      {item.title}
                    </AccordionTrigger>
                    <AccordionContent className="text-base leading-relaxed text-white/66" data-testid={`objection-answer-${index + 1}`}>
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        <section className="logic-section" id="oferta">
          <div className="logic-shell">
            <div className="logic-panel reveal-block rounded-[2.25rem] p-6 md:p-10" data-reveal="true" data-testid="offer-panel">
              <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
                <div className="text-center">
                  <h2 className="mt-5 text-4xl font-semibold leading-tight text-white sm:text-5xl" data-testid="offer-title">
                    Entre agora no Curso Completo de Logic Pro.
                  </h2>
                  <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/76 md:text-lg" data-testid="offer-description">
                    Tenha um caminho claro para aprender o software, acelerar sua produção e desenvolver um fluxo consistente com gravação, edição, mixagem, masterização e recursos atuais do Logic Pro.
                  </p>

                  <div className="mt-8 grid gap-3 sm:grid-cols-3" data-testid="offer-bonuses-grid">
                    {bonuses.map((bonus, index) => (
                  <div key={bonus.title} className="rounded-[1.25rem] border border-white/10 bg-white/5 px-4 py-4 text-center" data-testid={`offer-bonus-${index + 1}`}>
                        <p className="text-base font-medium text-white" data-testid={`offer-bonus-title-${index + 1}`}>
                          {bonus.title} {bonus.highlight ? <span className="text-cyan-200">{bonus.highlight}</span> : null}
                        </p>
                        <p className="mt-2 text-sm leading-relaxed text-white/88" data-testid={`offer-bonus-subtitle-${index + 1}`}>
                          {bonus.subtitle}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <Card className="rounded-[2rem] border border-white/10 bg-[#090b10]/95 shadow-none" data-testid="offer-price-card">
                  <CardContent className="p-6 md:p-8">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm uppercase tracking-[0.28em] text-white/42" data-testid="offer-price-label">
                          Investimento
                        </p>
                        <p className="mt-3 text-5xl font-semibold text-white md:text-6xl" data-testid="offer-price-value">
                          R$ 139,90
                        </p>
                        <p className="mt-3 text-xl font-semibold text-cyan-100" data-testid="offer-installments">
                          12x de R$ 14,47
                        </p>
                        <p className="mt-2 text-base text-white/88" data-testid="offer-installments-note">
                          Parcelamento disponível no checkout Hotmart.
                        </p>
                      </div>
                      <span className="rounded-full border border-emerald-300/20 bg-emerald-300/10 px-3 py-2 text-sm text-emerald-200" data-testid="offer-guarantee-badge">
                        7 dias de garantia
                      </span>
                    </div>

                    <div className="mt-8 space-y-3" data-testid="offer-includes-list">
                      {[
                        "Acesso imediato ao curso completo",
                        "Módulos práticos do setup à finalização",
                        "Aulas sobre IA e workflow moderno no Logic",
                        "Bônus com templates e atalhos",
                      ].map((item, index) => (
                        <div key={item} className="flex items-start gap-3" data-testid={`offer-includes-item-${index + 1}`}>
                          <span className="mt-1 rounded-full border border-cyan-300/30 bg-cyan-300/10 p-1 text-cyan-200">
                            <Check className="h-3.5 w-3.5" />
                          </span>
                          <p className="text-sm leading-relaxed text-white md:text-base">{item}</p>
                        </div>
                      ))}
                    </div>

                    <CheckoutButton testId="offer-primary-cta-button" className="mt-8 w-full">
                      👉 Quero começar agora <ArrowRight className="ml-2 h-4 w-4" />
                    </CheckoutButton>

                    <p className="mt-4 text-center text-xs uppercase tracking-[0.24em] text-white/56" data-testid="offer-checkout-note">
                      Checkout oficial Hotmart
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="logic-section pt-0" id="garantia">
          <div className="logic-shell">
            <div className="reveal-block grid gap-5 md:grid-cols-3" data-reveal="true">
              <Card className="logic-panel rounded-[1.75rem] border border-white/12 bg-white/[0.06]" data-testid="guarantee-card-1">
                <CardContent className="p-6 text-center">
                  <ShieldCheck className="h-6 w-6 text-cyan-200" />
                  <p className="mt-4 text-xl font-medium text-white" data-testid="guarantee-title">7 dias de garantia total</p>
                  <p className="mt-3 text-sm leading-relaxed text-white" data-testid="guarantee-text">
                    Você entra com segurança para conhecer o método e sentir a clareza da estrutura por conta própria.
                  </p>
                </CardContent>
              </Card>

              <Card className="logic-panel rounded-[1.75rem] border border-white/12 bg-white/[0.06]" data-testid="guarantee-card-2">
                <CardContent className="p-6 text-center">
                  <CirclePlay className="h-6 w-6 text-cyan-200" />
                  <p className="mt-4 text-xl font-medium text-white" data-testid="guarantee-title-2">Aprendizado aplicável</p>
                  <p className="mt-3 text-sm leading-relaxed text-white" data-testid="guarantee-text-2">
                    Nada de teoria solta. O foco é te fazer entender o Logic enquanto produz, organiza e finaliza.
                  </p>
                </CardContent>
              </Card>

              <Card className="logic-panel rounded-[1.75rem] border border-white/12 bg-white/[0.06]" data-testid="guarantee-card-3">
                <CardContent className="p-6 text-center">
                  <Sparkles className="h-6 w-6 text-cyan-200" />
                  <p className="mt-4 text-xl font-medium text-white" data-testid="guarantee-title-3">Fluxo moderno</p>
                  <p className="mt-3 text-sm leading-relaxed text-white" data-testid="guarantee-text-3">
                    Gravação, edição, mixagem e IA integrados num workflow direto para o produtor de hoje.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="logic-section pt-0">
          <div className="logic-shell">
            <div className="reveal-block rounded-[2.5rem] border border-white/10 bg-white/[0.03] px-6 py-10 text-center md:px-10 md:py-14" data-reveal="true" data-testid="final-cta-panel">
              <p className="text-sm uppercase tracking-[0.34em] text-white/40" data-testid="final-cta-label">
                Último passo
              </p>
              <h2 className="mx-auto mt-5 max-w-4xl text-4xl font-semibold leading-tight text-white sm:text-5xl" data-testid="final-cta-title">
                Se você quer aprender Logic Pro com método completo, esta é a próxima etapa.
              </h2>
              <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/76 md:text-lg" data-testid="final-cta-description">
                Curso completo de Logic Pro com foco em produção musical, afinação vocal, gravação, edição, mixagem e construção de workflow profissional.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
                <CheckoutButton testId="final-primary-cta-button">👉 Quero começar agora</CheckoutButton>
                <SecondaryLink href="#hero" testId="final-secondary-link">Voltar ao topo</SecondaryLink>
              </div>
            </div>
          </div>
        </section>

        <footer className="logic-shell pb-28 pt-2 text-center text-sm text-white/62" data-testid="footer-credit">
          Desenvolvido por Mariano Júnior.
        </footer>
      </main>

      <a
        href={whatsappUrl}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-6 left-6 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-[0_18px_45px_rgba(37,211,102,0.35)] transition duration-300 hover:scale-105 max-md:bottom-[6.2rem] max-md:left-auto max-md:right-4"
        data-testid="whatsapp-floating-button"
        aria-label="Falar no WhatsApp"
      >
        <MessageCircle className="h-6 w-6" />
      </a>

      <div className="sticky-mobile-cta" data-testid="mobile-sticky-cta">
        <div className="logic-panel flex items-center justify-between gap-4 rounded-[1.6rem] p-4">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-white/38" data-testid="mobile-sticky-label">Oferta</p>
            <p className="text-lg font-semibold text-white" data-testid="mobile-sticky-price">R$ 139,90</p>
          </div>
          <CheckoutButton testId="mobile-sticky-button" className="h-11 px-5 text-xs sm:text-sm">
            Comprar agora
          </CheckoutButton>
        </div>
      </div>
    </div>
  );
};